import React, {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { HasPermission } from "../../components/AccessControl/ProtectedComponent";

import { useCandidates } from "../../contexts/CandidateContext";
import { useCampaigns } from "../../contexts/CampaignsContext";
import Modal from "../../components/UI/Modal";
import CandidateForm from "../../components/Form/CandidateForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import ReactPaginate from "react-paginate";
import { MoreVertical, X } from "lucide-react";
import { createPortal } from "react-dom";
import StatusBadge from "../../components/UI/StatusBadge";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";

const Candidates = React.memo(() => {
  const {
    pagination,
    candidates,
    fetchCandidates,
    addCandidate,
    updateCandidate,
    deleteCandidate,
    loading,
  } = useCandidates();
  const { campaigns } = useCampaigns();
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const buttonRefs = useRef({});
  const dropdownRef = useRef(null);
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);

  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState("");
  const [campaignNames, setCampaignNames] = useState([]);
  const [loadingCampaignNames, setLoadingCampaignNames] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null means use normal fetch, object means use search results
  const searchTimeoutRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId === null) return;

      const button = buttonRefs.current[openDropdownId];
      const dropdown = dropdownRef.current;

      // Check if click is outside both button and dropdown
      if (
        button &&
        dropdown &&
        !button.contains(event.target) &&
        !dropdown.contains(event.target)
      ) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openDropdownId]);

  const handleDropdownToggle = useCallback(
    (requestId, event) => {
      event.stopPropagation();
      if (openDropdownId === requestId) {
        setOpenDropdownId(null);
      } else {
        setOpenDropdownId(requestId);
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();
        const dropdownWidth = 150;
        const dropdownHeight = 100;
        const gap = 4;

        let x = rect.right - dropdownWidth;
        let y = rect.bottom + gap;

        if (x < 8) {
          x = Math.max(8, rect.left);
        }

        if (y + dropdownHeight > window.innerHeight - 8) {
          y = rect.top - dropdownHeight - gap;
        }

        if (y < 8) {
          y = 8;
        }

        if (x < 8) {
          x = 8;
        }

        setDropdownCoords({ x, y });
      }
    },
    [openDropdownId],
  );

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingCandidate(null);
    setIsModalOpen(true);
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((candidate) => {
    setEditingCandidate(candidate);
    setIsModalOpen(true);
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingCandidate(null);
    setFormErrors({});
  }, []);

  const convertFormDataToObject = (formData) => {
    const entries = formData.entries();
    const result = {};

    for (const [key, value] of entries) {
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !(typeof value === "object" && Object.keys(value).length === 0)
      ) {
        result[key] = value;
      }
    }

    return result;
  };

  const handleFormSubmit = useCallback(
    async (candidate) => {
      try {
        if (editingCandidate) {
          // Convert FormData to object
          const candidateData =
            candidate instanceof FormData
              ? convertFormDataToObject(candidate)
              : candidate;

          await updateCandidate({ ...editingCandidate, ...candidateData });
          // setNotification("Candidate updated successfully.");
          setNotification({
            message: "Candidate updated successfully.",
            type: "success",
          });
        } else {
          await addCandidate(candidate);
          setNotification({
            message: "Candidate added successfully.",
            type: "success",
          });
          await fetchCandidates(1);
        }
        closeModal();
      } catch (err) {
        const apiData = err?.response?.data || err;
        const fieldErrors = apiData?.errors || err?.errors || {};
        const message =
          apiData?.message ||
          err?.message ||
          "Something went wrong. Please check the form and try again.";
        setFormErrors(typeof fieldErrors === "object" ? fieldErrors : {});
        setNotification({ message, type: "error" });
      }
    },
    [
      addCandidate,
      updateCandidate,
      closeModal,
      fetchCandidates,
      editingCandidate,
    ],
  );

  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset to page 1 when search query or campaign filter changes
  useEffect(() => {
    if (
      currentPage !== 1 &&
      (debouncedSearchQuery.trim() || selectedCampaignFilter)
    ) {
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, selectedCampaignFilter, currentPage]);

  // Get selected campaign name for filtering
  const selectedCampaignName = useMemo(() => {
    if (!selectedCampaignFilter) return null;
    const campaign = campaignNames.find(
      (c) => String(c.id) === String(selectedCampaignFilter),
    );
    return campaign?.campaign_name || null;
  }, [selectedCampaignFilter, campaignNames]);

  // Search candidates API call - triggers when campaign is selected or search query changes
  useEffect(() => {
    const searchCandidates = async () => {
      if (!debouncedSearchQuery.trim() && !selectedCampaignFilter) {
        // If no search query and no campaign filter, clear search results
        // Component will use normal candidates from context
        setSearchResults(null);
        return;
      }

      try {
        setIsSearching(true);
        const token = getToken();
        const params = new URLSearchParams();

        // Add search query if present
        if (debouncedSearchQuery.trim()) {
          params.append("search", debouncedSearchQuery.trim());
        }

        // Add pagination
        params.append("page", currentPage.toString());
        params.append("per_page", pagination.perPage.toString() || "15");

        const response = await axios.get(
          `https://hrmspayroll.prepstripe.com/api/candidates/search?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data?.success) {
          const searchData = response.data.data;

          // Filter by campaign name if selected (client-side filtering after API search)
          // Match by campaign_name from the campaigns array in candidate response
          let filteredData = searchData.data || [];
          const hasCampaignFilter =
            selectedCampaignName && filteredData.length > 0;

          if (hasCampaignFilter) {
            filteredData = filteredData.filter((candidate) => {
              const candidateCampaignName =
                candidate.campaigns?.[0]?.campaign_name;
              return (
                candidateCampaignName &&
                candidateCampaignName === selectedCampaignName
              );
            });
          }

          setSearchResults({
            data: filteredData,
            pagination: {
              currentPage: searchData.current_page || 1,
              // When campaign filter is applied, we can't accurately calculate totalPages
              // as we're only filtering the current page. Show 1 page if filtered results are empty,
              // otherwise show the original totalPages (which may be inaccurate)
              totalPages: hasCampaignFilter
                ? filteredData.length > 0
                  ? searchData.last_page || 1
                  : 1
                : searchData.last_page || 1,
              totalItems: hasCampaignFilter
                ? filteredData.length > 0
                  ? searchData.total || 0
                  : 0
                : searchData.total || 0,
              perPage: searchData.per_page || 15,
            },
          });
        } else {
          setSearchResults({
            data: [],
            pagination: {
              currentPage: 1,
              totalPages: 1,
              totalItems: 0,
              perPage: pagination.perPage || 15,
            },
          });
        }
      } catch (error) {
        console.error("Error searching candidates:", error);
        setNotification({
          message: "Failed to search candidates",
          type: "error",
        });
        setSearchResults({
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            perPage: pagination.perPage || 15,
          },
        });
      } finally {
        setIsSearching(false);
      }
    };

    searchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchQuery,
    selectedCampaignFilter,
    selectedCampaignName,
    currentPage,
    pagination.perPage,
  ]);

  // Fetch campaign names from API
  useEffect(() => {
    const fetchCampaignNames = async () => {
      try {
        setLoadingCampaignNames(true);
        const token = getToken();
        const response = await axios.get(
          "https://hrmspayroll.prepstripe.com/api/campaign-names/list",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setCampaignNames(response.data?.campaigns || []);
      } catch (error) {
        console.error("Error fetching campaign names:", error);
        setNotification({
          message: "Failed to load campaign names",
          type: "error",
        });
      } finally {
        setLoadingCampaignNames(false);
      }
    };
    fetchCampaignNames();
  }, []);

  // Memoize campaigns for dropdown options
  const campaignOptions = useMemo(
    () => campaigns.map((c) => ({ value: c.id, label: c.campaign_name })),
    [campaigns],
  );

  // Memoize candidates with campaign names for the table
  const candidatesWithCampaigns = useMemo(() => {
    const candidatesToUse = searchResults?.data || candidates;
    if (!candidatesToUse) return [];
    const list = searchResults?.data
      ? [...candidatesToUse].reverse()
      : candidatesToUse;
    return list.map((candidate) => ({
      ...candidate,
      campaign_name:
        candidate.campaigns?.[0]?.campaign_name || "Unknown Campaign",
    }));
  }, [candidates, searchResults]);

  // Get pagination data (use search results pagination if available, otherwise use context pagination)
  const tablePagination = useMemo(() => {
    if (searchResults) {
      return searchResults.pagination;
    }
    return pagination;
  }, [searchResults, pagination]);

  const handleDeleteCandidate = useCallback(
    async (id) => {
      try {
        await deleteCandidate(id);
        setNotification({
          message: "Candidate deleted successfully",
          type: "success",
        });

        // Update search results if we're in search mode
        if (searchResults) {
          setSearchResults((prev) => {
            if (!prev) return null;
            const updatedData = prev.data.filter(
              (candidate) => candidate.id !== id,
            );
            return {
              ...prev,
              data: updatedData,
              pagination: {
                ...prev.pagination,
                totalItems: updatedData.length,
                totalPages: Math.ceil(
                  updatedData.length / prev.pagination.perPage,
                ),
              },
            };
          });
        }
      } catch (error) {
        setNotification({
          message: "Failed to delete candidate",
          type: "error",
        });
      }
    },
    [deleteCandidate, searchResults],
  );

  // Table columns
  const getColumns = useMemo(() => {
    return (currentPage, pageSize) => [
      {
        label: "Sr. No",
        field: "sr_no",
        align: "center",
        render: (_row, idx) => (currentPage - 1) * pageSize + idx + 1,
      },
      { label: "Name", field: "name", align: "left" },
      { label: "Email", field: "email", align: "left" },
      {
        label: "Campaign Name",
        field: "campaign_id",
        align: "left",
        render: (row) => row.campaigns?.[0]?.campaign_name || "-", // Access campaign_name from campaigns array
      },
      {
        label: "Status",
        field: "status",
        align: "center",
        render: (row) => (
          <StatusBadge status={row.campaigns?.[0]?.pivot?.status || "-"} />
        ),
      },
      {
        label: "Actions",
        field: "actions",
        align: "center",
        render: (row) => (
          <div className="flex items-center justify-center">
            <div className="relative dropdown-container">
              <button
                ref={(el) => {
                  if (el) buttonRefs.current[row.id] = el;
                }}
                onClick={(e) => handleDropdownToggle(row.id, e)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            {openDropdownId === row.id &&
              createPortal(
                <div
                  ref={dropdownRef}
                  style={{
                    position: "fixed",
                    top: dropdownCoords.y,
                    left: dropdownCoords.x,
                    zIndex: 9999,
                  }}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[150px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className=" flex flex-col space-y-0">
                    {/* Edit */}
                    <HasPermission
                      permission={[
                        "candidates_update",
                        "candidate_update",
                        "Candidate Update",
                        "Candidate_Update",
                        "all_candidates_update",
                      ]}
                    >
                      <button
                        onClick={() => {
                          openModalForEdit(row);
                          setOpenDropdownId(null);
                        }}
                        className="text-blue-600 hover:text-blue-800 w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                      >
                        Edit
                      </button>
                    </HasPermission>
                    {/* Delete */}
                    <HasPermission
                      permission={[
                        "candidates_delete",
                        "candidate_delete",
                        "Candidate Delete",
                        "Candidate_Delete",
                        "all_candidates_delete",
                      ]}
                    >
                      <DeleteButton
                        className="text-left text-red-600 text-sm hover:text-red-800 px-3 py-2"
                        onDelete={() => {
                          handleDeleteCandidate(row.id);
                          setOpenDropdownId(null);
                        }}
                      />
                    </HasPermission>
                  </div>
                </div>,
                document.body,
              )}
          </div>
        ),
      },
    ];
  }, [
    openModalForEdit,
    openDropdownId,
    dropdownCoords,
    handleDeleteCandidate,
    handleDropdownToggle,
  ]);

  const handlePageChange = (newPage) => {
    const totalPages =
      searchResults?.pagination?.totalPages || pagination.totalPages;
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // If we have search results, the useEffect will handle the API call
      // Otherwise, fetch normal candidates
      if (
        !searchResults &&
        !debouncedSearchQuery.trim() &&
        !selectedCampaignFilter
      ) {
        fetchCandidates(newPage);
      }
    }
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          Candidates
        </h2>
        <HasPermission
          permission={[
            "candidates_create",
            "candidate_create",
            "Candidate Create",
            "Candidate_Create",
            "all_candidates_create",
          ]}
        >
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 text-white font-medium px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
          >
            Add Candidate
          </button>
        </HasPermission>
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-2 justify-between items-start mb-4">
        {/* Search field */}
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full placeholder:text-xs dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 md:placeholder:text-sm p-2 pr-8 border border-gray-300 rounded-md"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                if (currentPage !== 1) {
                  setCurrentPage(1);
                }
              }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Campaign filter */}
        <div className="w-full sm:w-64">
          <select
            value={selectedCampaignFilter}
            onChange={(e) => {
              setSelectedCampaignFilter(e.target.value);
              // Reset to page 1 when filter changes
              if (currentPage !== 1) {
                setCurrentPage(1);
              }
            }}
            className="w-full p-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none text-sm border border-gray-300 rounded-md"
            disabled={loadingCampaignNames}
          >
            <option value="">All Campaigns</option>
            {campaignNames.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.campaign_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Candidates Table */}
      <div>
        <ReusableTable
          data={candidatesWithCampaigns}
          columns={getColumns(currentPage, tablePagination.perPage)}
          loading={loading || isSearching}
          filterable={false}
        />

        {/* Pagination using React Paginate */}
        {candidatesWithCampaigns?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="pointer-events-none"
            pageCount={tablePagination.totalPages}
            previousLabel="< Prev"
            previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingCandidate ? "Edit Candidate" : "Add Candidate"}
      >
        <CandidateForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          campaignOptions={campaignOptions}
          initialValues={
            editingCandidate
              ? {
                  ...editingCandidate,
                  campaign_id: editingCandidate.campaigns?.[0]?.id || "",
                  campaign_ids:
                    editingCandidate.campaigns?.map((c) => c.id) || [],
                  pan_number: editingCandidate.pan_number || "",
                  aadhaar_number: editingCandidate.aadhaar_number || "",
                  application_form_id:
                    editingCandidate.application_form_id || "",
                }
              : {
                  campaign_id: "",
                  campaign_ids: [],
                  name: "",
                  email: "",
                  status: "",
                  pan_number: "",
                  aadhaar_number: "",
                  application_form_id: "",
                }
          }
          errors={formErrors}
          notification={notification}
          setNotification={setNotification}
        />
      </Modal>
    </div>
  );
});

export default Candidates;
